import { useState, useRef } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function ImageUpload({ value, onChange, folder = "images", label = "Image", className = "" }) {
  const [uploading, setUploading] = useState(false);
  const ref = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Image must be under 10MB"); return; }

    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("folder", folder);
    try {
      const { data } = await http.post("/upload/file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (ref.current) ref.current.value = "";
    }
  };

  const imageUrl = value?.startsWith("/api/") ? `${API}${value}` : value;

  return (
    <div className={className} data-testid="image-upload">
      <label className="text-zinc-400 text-[10px] uppercase tracking-wider block mb-1">{label}</label>
      {value ? (
        <div className="relative rounded-lg overflow-hidden border border-white/10 bg-black">
          <img src={imageUrl} alt="" className="w-full h-40 object-cover" />
          <button onClick={() => onChange("")} className="absolute top-2 right-2 bg-black/70 text-white rounded-full p-1 hover:bg-red-600 transition-colors" data-testid="image-upload-remove">
            <X size={14} />
          </button>
        </div>
      ) : (
        <button onClick={() => ref.current?.click()} disabled={uploading}
          className="w-full h-32 rounded-lg border-2 border-dashed border-white/10 hover:border-[#C9A055]/40 flex flex-col items-center justify-center gap-2 transition-colors disabled:opacity-50"
          data-testid="image-upload-btn">
          {uploading ? <Loader2 size={20} className="animate-spin text-[#C9A055]" /> : <Upload size={20} className="text-zinc-500" />}
          <span className="text-zinc-500 text-xs">{uploading ? "Uploading..." : "Click to upload"}</span>
          <span className="text-zinc-600 text-[10px]">JPG, PNG, WebP · Max 10MB</span>
        </button>
      )}
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={handleUpload} className="hidden" />
    </div>
  );
}
